import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {
  getIsSyntheticTest,
  getLanguageCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import {getAnchor, getSearchParameters} from 'modules/sapiSearch/selectors'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  selectIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'

import {SUPPORTED_HOPPER_AD_COUNTRIES} from '../constants'
import {getSearchPlaceId} from '../getSearchPlaceId'

type HopperAdType = 'Carousel' | 'Banner'

interface HopperAdEligibility {
  isEligible: boolean
  placeId: string | undefined
}

/**
 * Determines whether the user is eligible to see Hopper ads.
 *
 * Eligibility requires all of:
 * - Brand supports Hopper ads
 * - Session is not a synthetic test (avoids impressions from automated runs)
 * - User language is English (all Hopper creatives are English)
 * - User is not authenticated, and auth has finished loading
 * - User country is one Hopper sells in (US or CA)
 * - Banner only: a placeId from the active search or the anchor
 */
export const useHopperAdEligibility = (
  adType: HopperAdType
): HopperAdEligibility => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isAuthLoading = useSelector(selectIsLoading)
  const languageCode = useSelector(getLanguageCode)
  const isSyntheticTest = useSelector(getIsSyntheticTest)
  const searchParameters = useSelector(getSearchParameters)
  const anchor = useSelector(getAnchor)
  const userCountry = useSelector(getUserCountryCode)
  const {
    monetisation: {showsHopperAds}
  } = useBrandCapabilities()

  const placeId = useMemo(
    () =>
      adType === 'Banner'
        ? getSearchPlaceId(searchParameters, anchor)
        : undefined,
    [searchParameters, anchor, adType]
  )

  const isEligible = useMemo(() => {
    if (isSyntheticTest || languageCode !== 'en' || !showsHopperAds) {
      return false
    }

    if (!SUPPORTED_HOPPER_AD_COUNTRIES?.has(userCountry ?? '')) return false

    if (isAuthLoading || isAuthenticated) return false

    return !(adType === 'Banner' && !placeId)
  }, [
    isSyntheticTest,
    isAuthLoading,
    isAuthenticated,
    languageCode,
    adType,
    placeId,
    showsHopperAds,
    userCountry
  ])

  return {
    isEligible,
    placeId
  }
}
